/**
 * Building Attribute rental status helper.
 *
 * A building attribute (e.g. parking spot, storage box) is linked to contracts
 * via `contractProducts`. The active phase of those contracts determines what
 * the rental team should see for each attribute:
 *   - RENTED:      attribute is held by an ACTIVE or CONCEPT contract
 *   - TERMINATION: attribute is in opzeg (TERMINATION_PHASE) and no rented overlap
 *   - VACANT:      no contracts, or only CLOSED contracts
 */

// The backend enum stores the *display* values as strings (see
// ContractPhase in rental-contract.entity.ts). The API echoes those values back
// to the frontend, so we match against them directly.
export const CONTRACT_PHASE = {
  CONCEPT: 'In concept',
  ACTIVE: 'Lopende contracten',
  TERMINATION: 'Contract in opzeg',
  CLOSED: 'Gesloten',
} as const;

export type AttributeRentalStatus = 'rented' | 'termination' | 'vacant';

export interface AttributeStatusConfig {
  key: AttributeRentalStatus;
  label: string;
  description: string;
  /** Tailwind background class for the status dot/pill. */
  bgClass: string;
  /** Tailwind text class for the label. */
  textClass: string;
  /** Soft background for the pill wrapper. */
  softBgClass: string;
  /** Border colour for outline pills. */
  borderClass: string;
}

export const ATTRIBUTE_STATUS_CONFIG: Record<AttributeRentalStatus, AttributeStatusConfig> = {
  rented: {
    key: 'rented',
    label: 'Verhuurd',
    description: 'Gekoppeld aan een actief of concept contract',
    bgClass: 'bg-status-good',
    textClass: 'text-status-good',
    softBgClass: 'bg-status-good/10',
    borderClass: 'border-status-good',
  },
  termination: {
    key: 'termination',
    label: 'In opzeg',
    description: 'Contract zit in de opzegperiode',
    bgClass: 'bg-status-medium',
    textClass: 'text-status-medium',
    softBgClass: 'bg-status-medium/10',
    borderClass: 'border-status-medium',
  },
  vacant: {
    key: 'vacant',
    label: 'Leegstaand',
    description: 'Niet gekoppeld aan een lopend contract',
    bgClass: 'bg-status-bad',
    textClass: 'text-status-bad',
    softBgClass: 'bg-status-bad/10',
    borderClass: 'border-status-bad',
  },
};

interface ContractProductLike {
  contract?: {
    phase?: string;
  };
}

interface AttributeLike {
  contractProducts?: ContractProductLike[] | null;
}

/**
 * Determine the rental status of a building attribute based on its
 * contractProducts. Throws are deliberately avoided — bad data results in
 * the safest "vacant" status so the page keeps rendering.
 */
export const getAttributeRentalStatus = (attribute: AttributeLike): AttributeRentalStatus => {
  const phases = (attribute?.contractProducts || [])
    .map((cp) => cp?.contract?.phase)
    .filter((p): p is string => Boolean(p));

  if (phases.length === 0) return 'vacant';

  const hasRented = phases.some(
    (p) => p === CONTRACT_PHASE.ACTIVE || p === CONTRACT_PHASE.CONCEPT,
  );
  if (hasRented) return 'rented';

  const hasTermination = phases.some((p) => p === CONTRACT_PHASE.TERMINATION);
  if (hasTermination) return 'termination';

  return 'vacant';
};

interface ContractProductWithUnit {
  contract?: {
    phase?: string;
    unit?: { id?: string };
  };
}

interface AttributeWithContractUnits {
  contractProducts?: ContractProductWithUnit[] | null;
}

/**
 * Return all unit ids tied to non-closed contracts on this attribute.
 * CLOSED contracts are ignored because they no longer represent a real link.
 */
export const getAttributeUnitIds = (attribute: AttributeWithContractUnits): string[] => {
  const products = attribute?.contractProducts || [];
  const ids = products
    .filter((cp) => cp?.contract?.phase && cp.contract.phase !== CONTRACT_PHASE.CLOSED)
    .map((cp) => cp?.contract?.unit?.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
  return Array.from(new Set(ids));
};

interface ContractContactLike {
  contactType?: string;
  resource?: { name?: string } | null;
}

interface ContractProductWithDetails {
  contract?: {
    id?: number;
    phase?: string;
    unit?: { id?: string } | null;
    contractContacts?: ContractContactLike[] | null;
  };
}

interface AttributeWithContractDetails {
  contractProducts?: ContractProductWithDetails[] | null;
}

export interface AttributeExistingContract {
  contractId?: number;
  phase: string;
  /** Derived status of that contract: 'rented' (active/concept) or 'termination' (in opzeg). */
  status: Exclude<AttributeRentalStatus, 'vacant'>;
  /** Tenant names (from the contract contacts), deduplicated, empty when unknown. */
  tenantNames: string[];
  /** Unit the contract belongs to; undefined for attribute-only (standalone) contracts. */
  unitId?: string;
}

// Which existing contract matters most when several non-closed contracts touch one attribute.
const EXISTING_CONTRACT_PRIORITY: Record<string, number> = {
  [CONTRACT_PHASE.ACTIVE]: 3,
  [CONTRACT_PHASE.TERMINATION]: 2,
  [CONTRACT_PHASE.CONCEPT]: 1,
};

/**
 * The most relevant non-closed contract on an attribute, for display in a contract wizard:
 * "this garage is still on contract X, phase 'Contract in opzeg'". Returns null when the
 * attribute is vacant (no contracts or only CLOSED ones). Never throws on bad data.
 */
export const getAttributeExistingContract = (
  attribute: AttributeWithContractDetails,
): AttributeExistingContract | null => {
  const candidates = (attribute?.contractProducts || [])
    .map((cp) => cp?.contract)
    .filter((c): c is NonNullable<typeof c> => Boolean(c?.phase) && c!.phase !== CONTRACT_PHASE.CLOSED)
    .filter((c) => EXISTING_CONTRACT_PRIORITY[c.phase as string] !== undefined);

  if (candidates.length === 0) return null;

  const winner = candidates.reduce((best, current) =>
    EXISTING_CONTRACT_PRIORITY[current.phase as string] > EXISTING_CONTRACT_PRIORITY[best.phase as string]
      ? current
      : best,
  );

  const tenantNames = Array.from(
    new Set(
      (winner.contractContacts || [])
        .map((cc) => cc?.resource?.name?.trim())
        .filter((name): name is string => Boolean(name)),
    ),
  );

  return {
    contractId: winner.id,
    phase: winner.phase as string,
    status: winner.phase === CONTRACT_PHASE.TERMINATION ? 'termination' : 'rented',
    tenantNames,
    unitId: winner.unit?.id || undefined,
  };
};
